import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Snackbar,
  Alert,
  Container,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from '@mui/material';
import { getRecentJobs } from '../../api/upload';
import gesLogo from './ges-logos.png';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1B3B58',
      light: '#81B3C0',
      dark: '#1B3B58',
    },
    secondary: {
      main: '#DD7C25',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.5px',
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
        },
      },
    },
  },
});

const JobsList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchJobs = async () => {
      try {
        const response = await getRecentJobs(10); // Limit to 10
        setJobs(response.jobs || []);
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load job history. Please try again.',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'primary';
      case 'error':
        return 'error';
      case 'queued':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography variant="h6">Loading job history...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box
          component="img"
          src={gesLogo}
          alt="GES Logo"
          sx={{
            position: 'absolute',
            top: 20,
            left: 20,
            height: 60,
            width: 'auto',
            objectFit: 'contain',
            zIndex: 10,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Paper
              elevation={0}
              sx={{
                p: 5,
                mb: 4,
                borderRadius: 4,
                background: 'rgba(255, 248, 248, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow:
                  '0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.5) inset',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow:
                    '0 30px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.5) inset',
                },
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1.5,
                    color: '#1B3B58',
                    fontWeight: 800,
                  }}
                >
                  Job History
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#1B3B58',
                    fontSize: '1rem',
                    maxWidth: 600,
                    mx: 'auto',
                  }}
                >
                  View recent file processing jobs and their status.
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: '#1B3B58' }}>Job ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1B3B58' }}>Filename</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1B3B58' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1B3B58' }}>Created At</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1B3B58' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobs.map((job) => (
                      <TableRow
                        key={job.job_id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(27, 59, 88, 0.04)',
                          },
                        }}
                      >
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {job.job_id}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {job.original_filename}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={job.status}
                            color={getStatusColor(job.status)}
                            size="small"
                            sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {formatDate(job.created_at)}
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`/jobs/${job.job_id}`}
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography
                              sx={{
                                color: '#DD7C25',
                                fontWeight: 600,
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              View Details
                            </Typography>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {jobs.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" sx={{ color: '#64748b' }}>
                    No jobs found.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            elevation={6}
            sx={{ borderRadius: 3, fontWeight: 600 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default JobsList;
